import { useMemo, useState } from "react";
import { Badge, Button, ModalShell } from "@vb/ui";
import { Link } from "react-router-dom";
import { hashServerSeed, isMine, rollMines, type RoundSpec } from "./engine.js";

interface FairnessModalProps {
  open: boolean;
  onClose: () => void;
  /** Public commitment from session launch — committed before any round opens. */
  serverSeedHash: string;
  /** Raw server seed. Local-sim only — in the planned backend version this is
   *  hidden until the round resolves and revealed via /rgs/.../seeds. */
  serverSeed: string;
  clientSeed: string;
  nonce: number;
  mineCount: number;
  /** Index list of tiles the player picked, in order. */
  picks: number[];
  /** Round outcome state — drives the "Recompute" verdict.
   *  `idle` is treated like a resolved round for verification (no in-flight
   *  picks to gate against); `midround` hides the server seed since in the
   *  planned backend flow it isn't revealed until resolution. */
  outcome: "idle" | "midround" | "win" | "bust";
}

/**
 * Pressure Plate's verify dialog. Shows the session-level commitment hash,
 * the round inputs (serverSeed + clientSeed + nonce + mineCount), and a one-
 * click recompute that re-derives the mine layout from the seeds and confirms
 * it matches what the player actually saw.
 *
 * Local-sim caveat: today the serverSeed is visible in the browser the whole
 * time (this is a single-player demo). When the backend ships, the modal's
 * data flow stays the same but seeds come from /rgs/pressure-plate/:id/seeds
 * AFTER the round resolves — same UX, real provably-fair guarantees.
 */
export function FairnessModal({
  open,
  onClose,
  serverSeedHash,
  serverSeed,
  clientSeed,
  nonce,
  mineCount,
  picks,
  outcome,
}: FairnessModalProps) {
  const [verdict, setVerdict] = useState<"idle" | "ok" | "fail">("idle");

  // The seed is revealed (and the mine layout becomes computable) ONLY after
  // a round resolves — same gate the planned backend `/seeds` endpoint will
  // enforce. Idle and midround MUST hide everything seed-derived to prevent
  // a player from peeking the mine layout before they've committed picks.
  const revealed = outcome === "win" || outcome === "bust";

  // Recompute the canonical mine positions from the seeds. Only run after
  // resolution — otherwise the unrevealed mines would land in the React tree
  // (visible via DevTools, even if not rendered).
  const recomputed = useMemo(() => {
    if (!revealed) return null;
    const spec: RoundSpec = { serverSeed, clientSeed, nonce, mineCount };
    const hash = hashServerSeed(serverSeed);
    const mines = rollMines(spec).slice().sort((a, b) => a - b);
    return { hash, mines };
  }, [revealed, serverSeed, clientSeed, nonce, mineCount]);

  const handleVerify = (): void => {
    if (!revealed || !recomputed) return;
    // Two checks:
    // 1. SHA-equivalent fingerprint of the serverSeed matches the committed hash
    // 2. Every player pick that landed on a "mine" reveal IS in the recomputed
    //    mine set, and every "safe" pick is NOT.
    const hashOk = recomputed.hash === serverSeedHash;
    const picksConsistent = picks.every((idx) => {
      const wasMine = recomputed.mines.includes(idx);
      const claimedMine = isMine({ serverSeed, clientSeed, nonce, mineCount }, idx);
      return wasMine === claimedMine;
    });
    setVerdict(hashOk && picksConsistent ? "ok" : "fail");
  };

  return (
    <ModalShell open={open} onClose={onClose} maxWidth={520} labelledBy="pp-fair-title">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Badge kind="fair">PROVABLY FAIR</Badge>
          <h3
            id="pp-fair-title"
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: 18,
              letterSpacing: "0.04em",
              color: "var(--fg-1)",
              textTransform: "uppercase",
            }}
          >
            Verify this session
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: "transparent",
            border: "none",
            color: "var(--fg-3)",
            fontSize: 22,
            lineHeight: 1,
            cursor: "pointer",
            padding: 0,
          }}
        >
          ×
        </button>
      </div>

      <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.55 }}>
        Mine positions for every round are derived from the server seed (committed
        before play), the client seed, the nonce, and the mine count. You can
        recompute them and confirm the round you played matches the seeds shown.
      </p>

      <Section label="Server seed hash (committed)" value={serverSeedHash} mono />
      <Section
        label="Server seed (revealed)"
        value={revealed ? serverSeed : "—"}
        mono
        muted={!revealed}
        helper={
          !revealed
            ? "Sealed until the round resolves. Showing it now would leak the mine layout."
            : undefined
        }
      />
      <Section label="Client seed" value={clientSeed} mono />
      <Row label="Nonce" value={`#${nonce}`} />
      <Row label="Mines" value={`${mineCount} / 25`} />
      {revealed && recomputed && (
        <Row
          label="Computed mine positions"
          value={recomputed.mines
            .map((i) => `${"ABCDE"[i % 5]}${Math.floor(i / 5) + 1}`)
            .join(" · ")}
        />
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 6 }}>
        <Button variant="gold" onClick={handleVerify} disabled={!revealed}>
          Recompute
        </Button>
        {revealed && verdict === "ok" && (
          <span style={{ color: "var(--green)", fontSize: 12, fontWeight: 700, letterSpacing: "0.06em" }}>
            ✓ HASH MATCHES · PICKS CONSISTENT
          </span>
        )}
        {revealed && verdict === "fail" && (
          <span style={{ color: "var(--red)", fontSize: 12, fontWeight: 700, letterSpacing: "0.06em" }}>
            ✗ MISMATCH — PLEASE REPORT
          </span>
        )}
        {!revealed && (
          <span style={{ color: "var(--fg-3)", fontSize: 12 }}>
            {outcome === "midround"
              ? "Round in progress — recompute unlocks at resolve."
              : "Place a bet and complete a round to verify."}
          </span>
        )}
      </div>

      <p style={{ margin: 0, fontSize: 11, color: "var(--fg-3)", lineHeight: 1.6 }}>
        The full math is published at{" "}
        <Link to="/security" style={{ color: "var(--cyan)" }}>
          /security
        </Link>
        . The engine source is open; you can re-derive any round outside our
        site using the seeds above.
      </p>
    </ModalShell>
  );
}

function Section({
  label,
  value,
  mono,
  muted,
  helper,
}: {
  label: string;
  value: string;
  mono?: boolean;
  muted?: boolean;
  helper?: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: mono ? "var(--font-mono)" : "var(--font-ui)",
          fontSize: 12,
          color: muted ? "var(--fg-3)" : "var(--fg-1)",
          wordBreak: "break-all",
        }}
      >
        {value}
      </span>
      {helper && (
        <span style={{ fontSize: 11, color: "var(--fg-3)", lineHeight: 1.5 }}>{helper}</span>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
      <span
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
        }}
      >
        {label}
      </span>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-1)" }}>
        {value}
      </span>
    </div>
  );
}
